import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Chart from "chart.js/auto";
import Modal from "react-modal";
import "./styles.css";
import { pdf } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Link } from "react-router-dom";
import InputMask from "react-input-mask";
import { API_BASE_URL } from "../config/config";
import { NumericFormat } from "react-number-format";
import {
  calculateHighFinalValues,
  calculateLowFinalValues,
  calculateALowValue,
  calculateBLowValue,
  calculateCLowValue,
  calculateMaxLossrange,
  calculateLossThresold,
  calculateDamageSeverityScore,
  calculateGrade,
  calculatePostRepairFMV,
  calculateInherentDV,
  calculateDiminishedValue,
  calculateMaxLossPercentage,
  calculateDamageToValue,
} from "./utils/formulas"; // Import additional formulas

import { left } from "@cloudinary/url-gen/qualifiers/textAlignment";

const DiminishedValueQuote = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const value = 1234567; // Example value
  const [finalHighValue, setFinalHighValue] = useState(0);
  const [finalLowValue, setFinalLowValue] = useState(0);
  const [repairCost, setRepairCost] = useState(0);
  const [laborHours, setLaborHours] = useState(0);
  const [structural, setStructural] = useState(false);
  const [airbag, setAirbag] = useState(false);
  const [diminishedValue, setDiminishedValue] = useState(null);
  const [vin, setVin] = useState("");
  const [vinData, setVinData] = useState(null);
  const [marketValueData, setMarketValueData] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState("");

  const fetchVINData = async (vin) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vin/${vin}`);
      if (!response.ok) {
        throw new Error("Failed to fetch VIN data");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching VIN data:", error);
      return null;
    }
  };

  const fetchMarketValue = async (vin) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/marketvalue/${vin}`);
      if (!response.ok) {
        throw new Error("Failed to fetch market value");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching market value:", error);
      return null;
    }
  };

  const handleVinFetch = async () => {
    if (!vin || vin.length < 17) {
      console.warn("VIN must be 17 characters.");
      return;
    }

    console.log(`Fetching data for VIN: ${vin}...`);

    const vinResult = await fetchVINData(vin);
    const marketValueResult = await fetchMarketValue(vin);

    console.log("VIN API raw response:", vinResult);
    console.log("Market Value API response:", marketValueResult);

    setVinData(vinResult);
    setMarketValueData(marketValueResult);

    // Set market values if available
    if (marketValueResult?.above && marketValueResult?.below) {
      setFinalHighValue(marketValueResult.above);
      setFinalLowValue(marketValueResult.below);
    }

    // Extract Year, Make, Model from vinResult.Results
    if (vinResult?.Results && Array.isArray(vinResult.Results)) {
      const getValue = (key) => vinResult.Results.find((entry) => entry.Variable === key)?.Value || "";

      const year = getValue("Model Year");
      const make = getValue("NCSA Make");
      const model = getValue("NCSA Model");

      const vehicleString = `${year} ${make} ${model}`.trim();
      console.log("🚗 Parsed vehicle info:", vehicleString); // ✅ Debug output

      setVehicleInfo(vehicleString);
    } else {
      console.warn("VIN result format invalid:", vinResult);
    }
  };

  useEffect(() => {
    if (!finalHighValue || !finalLowValue) return;

    const maxLossRange = calculateMaxLossrange(finalHighValue, 0, 0, 0, finalLowValue, 0, 0, 0);

    const damageToValue = repairCost / finalHighValue;
    const damageSeverityScore = calculateDamageSeverityScore(laborHours, repairCost, damageToValue, structural, airbag);

    const inherentDV = calculateInherentDV(maxLossRange, damageSeverityScore);
    const postRepairFMV = calculatePostRepairFMV(finalHighValue, inherentDV);
    const dv = calculateDiminishedValue(finalHighValue, postRepairFMV);

    console.log("🧮 DV Calculation Debug:");
    console.log("Max Loss Range:", maxLossRange);
    console.log("Damage Severity Score:", damageSeverityScore);
    console.log("Inherent DV:", inherentDV);
    console.log("Post Repair FMV:", postRepairFMV);
    console.log("Diminished Value:", dv);

    setDiminishedValue(dv);
  }, [finalHighValue, finalLowValue, repairCost, laborHours, structural, airbag]);

  useEffect(() => {
    if (vin.length === 17) {
      handleVinFetch();
    }
  }, [vin]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  return (
    <div
      style={{
        background: "linear-gradient(237deg, rgba(39, 39, 39, 0) 70%, rgba(0, 195, 116, 0.4) 120%), #272727",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        minHeight: "100vh", // This is key: makes it fill the screen vertically
      }}
    >
      <header className="headerDvq">
        <a href="/">
          <img src="/ae.svg" alt="AELogoImage" className="logoDvq" />
        </a>
      </header>

      <div className="dvqPageWrapper">
        <div className="dvqPageContent">
          <div className="titleDvq">
            <h1 className="h1dvq">Diminished Value Quote</h1>
            <p className="subtextDvq">Fill out the form and generate your diminished value quote.</p>
          </div>

          <p className="claimInformationDvq">Claim Information</p>

          <div>
            <div className="yearVinDivDvq">
              <span className="spansDvq">VIN</span>
              <input className="biggerInputDvq" placeholder="Vin Number" maxLength="17" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} />
              <span className="spansDvq">Year / Make / Model</span>
              <input className="biggerInputDvq" placeholder="Vehicle Info" value={vehicleInfo} />
            </div>

            <div className="twoPerRowWrapper">
              <div className="inputGroup">
                <span className="spansDvq">Labor Hours</span>
                <input
                  className="halfInputDvq"
                  placeholder="Hours"
                  type="number"
                  value={laborHours === 0 ? "" : laborHours}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLaborHours(val === "" ? 0 : parseFloat(val));
                  }}
                />
              </div>

              <div className="inputGroup">
                <span className="spansDvq">Repair Cost</span>
                <NumericFormat
                  className="halfInputDvq"
                  placeholder="e.g. 2300"
                  value={repairCost === 0 ? "" : repairCost}
                  thousandSeparator=","
                  prefix="$"
                  allowNegative={false}
                  onValueChange={(values) => {
                    setRepairCost(values.floatValue || 0);
                  }}
                />
              </div>
            </div>

            <div className="inputGroupCheckboxesDvq">
              <label className="checkboxLabelDvq">
                <input type="checkbox" checked={structural} onChange={(e) => setStructural(e.target.checked)} />
                <span className="spansDvq">Structural Damage</span>
              </label>
            </div>

            <div className="inputGroupCheckboxesDvq">
              <label className="checkboxLabelDvq">
                <input type="checkbox" checked={airbag} onChange={(e) => setAirbag(e.target.checked)} />
                <span className="spansDvq">Airbag Deployed</span>
              </label>
            </div>
          </div>
          <button className="finalDvqButton" onClick={openModal}>
            <span className="calculateButtonTextDvq">Calculate</span>
          </button>
        </div>
      </div>

      <div>
        <Modal isOpen={isModalOpen} onRequestClose={closeModal} contentLabel="Calculation Modal" className="custom-modal-dvq" overlayClassName="custom-overlay">
          <header className="headerDvq-modal">
            <img src="/ae.svg" alt="AELogoImage" className="logoDvq" />
          </header>

          <button className="close-button-dvq" onClick={closeModal}>
            <span>X</span>
          </button>

          {/* 👇 This is the new flex wrapper to center the red box */}
          <div className="dvqModalWrapper">
            <div className="dvqModal">
              <div className="firstDivDvq">
                <h1 className="h1Dvq">Diminished Value Quote</h1>
                <span className="spanDvq">Click the box below to copy the DV amount.</span>
              </div>
              <div className="secondDivDvq">
                <span className="dvDvqText">Diminished Value</span>
                <span className="priceTextDvq">
                  {" "}
                  {diminishedValue
                    ? diminishedValue.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                      })
                    : "U$ Calculated Value"}
                </span>
              </div>
              <div className="thirdDivDvq">
                <button className="firstButtonDvq">
                  <span className="textInsideButtonsDvq">Edit</span>
                </button>
                <button className="secondButtonDvq">
                  <span className="textInsideButtonsDvq">New Quote</span>
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default DiminishedValueQuote;
